import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { vendorSlaService } from '../services/vendorSla.service';
import LoadingSpinner from '../components/common/LoadingSpinner';

const age = (iso) => {
    if (!iso) return '—';
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
};

const DispatchQueue = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState('open');
    const [rows, setRows] = useState([]);
    const [openCount, setOpenCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const load = useCallback(async () => {
        try {
            const res = await vendorSlaService.breaches(status);
            setRows(Array.isArray(res?.items) ? res.items : []);
            setOpenCount(res?.open_count ?? 0);
        } catch (e) {
            setMessage(e.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        setLoading(true);
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const resolve = async (id, resolution) => {
        try {
            await vendorSlaService.resolveBreach(id, { resolution });
            setMessage('Breach resolved');
            await load();
        } catch (e) {
            setMessage(e.message || 'Resolve failed');
        }
    };

    return (
        <div className="p-4 h-full">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <h1 className="text-xl font-semibold">Dispatch Queue</h1>
                    <span className="text-xs text-gray-500">{openCount} open breach{openCount === 1 ? '' : 'es'}</span>
                </div>
                <button type="button" className="px-3 py-1.5 border rounded text-sm" onClick={load}>
                    <RefreshCw className="w-4 h-4 inline mr-1" />Refresh
                </button>
            </div>

            <div className="flex gap-3 mb-3 text-sm">
                <select className="border rounded px-2 py-1" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="open">Open</option>
                    <option value="resolved">Resolved</option>
                    <option value="all">All</option>
                </select>
                {message && <span className="self-center text-gray-600">{message}</span>}
            </div>

            <div className="border rounded bg-white overflow-auto">
                {loading && <div className="p-8 flex justify-center"><LoadingSpinner /></div>}
                {!loading && (
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-gray-500 bg-gray-50">
                            <tr>
                                <th className="text-left px-3 py-2">Service</th>
                                <th className="text-left px-3 py-2">Type</th>
                                <th className="text-right px-3 py-2">Rounds</th>
                                <th className="text-right px-3 py-2">Max radius</th>
                                <th className="text-right px-3 py-2">Opened</th>
                                <th className="text-left px-3 py-2">State</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((b) => (
                                <tr key={b.id} className={`border-t ${b.is_open ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                                    <td className="px-3 py-2">
                                        <button
                                            type="button"
                                            className="text-blue-600 hover:underline"
                                            onClick={() => navigate(`/add-service?service_id=${b.service_id}${b.case_id ? `&case_id=${b.case_id}` : ''}`)}
                                        >
                                            {b.service_number || `#${b.service_id}`}
                                        </button>
                                    </td>
                                    <td className="px-3 py-2">{b.breach_type}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{b.rounds_tried ?? '—'}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{b.max_radius_km == null ? '—' : `${b.max_radius_km} km`}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{age(b.opened_at)}</td>
                                    <td className="px-3 py-2">
                                        {b.is_open ? (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                                {b.status || 'open'}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-500">{b.resolution}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        {b.is_open && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="text-xs text-green-700 hover:underline mr-3"
                                                    onClick={() => resolve(b.id, 'manual_assign')}
                                                >
                                                    Mark assigned
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-xs text-gray-500 hover:underline"
                                                    onClick={() => resolve(b.id, 'cancelled')}
                                                >
                                                    Cancelled
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && (
                                <tr>
                                    <td colSpan={7} className="px-3 py-8 text-center text-gray-400">
                                        {status === 'open' ? 'No open breaches — dispatch is healthy.' : 'Nothing here.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default DispatchQueue;
